import type { INetwork } from '../types';
import { md5 } from '@roai/shared';

function getIpAddress(timeout = 1000): Promise<string[]> {
  const ipv4Regex = /\b((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\b/;
  const ipv6Regex = /\b(?:[A-F0-9]{1,4}:){7}[A-F0-9]{1,4}\b/i;
  const ipSet = new Set<string>();

  return new Promise((resolve, reject) => {
    const conn = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });

    let timer: Maybe<ReturnType<typeof setTimeout>> = undefined;
    let isResolved = false;

    const cleanup = () => {
      if (isResolved) return;
      isResolved = true;
            
      clearTimeout(timer);
      conn.removeEventListener('icecandidate', onicecandidate);
      // eslint-disable-next-line
      try { conn.close(); } catch {}
    };

    const onicecandidate = (ice) => {
      if (isResolved) return;
            
      const candidate = ice?.candidate?.candidate;
      if (!candidate) {
        // No more candidates, resolve immediately
        resolveIPs();
        return;
      }

      for (const regex of [ipv4Regex, ipv6Regex]) {
        const [ip] = candidate.match(regex) ?? [];
        if (ip) ipSet.add(ip);
      }
    };

    const resolveIPs = () => {
      cleanup();
      resolve([...ipSet]);
    };

    conn.addEventListener('icecandidate', onicecandidate);
    conn.createDataChannel('');
    conn.createOffer()
      .then(offer => conn.setLocalDescription(offer))
      .catch(err => {
        cleanup();
        reject(err);
      });

    // Fallback timeout
    timer = setTimeout(resolveIPs, timeout);
  });
}

export async function getNetworkInfo(gdpr = false): Promise<INetwork> {
  const connection = navigator.connection;
  let ips: string[] = [];
  try {
    ips = await getIpAddress();
  } catch (e) {
    ips = [];
  }

  return {
    ca: connection && connection.type  ? connection.type : 'unknown',
    cb: connection && connection.effectiveType ? connection.effectiveType : 'unknown',
    cc: connection && connection.downlink ? String(connection.downlink) : 'unknown',
    cd: connection && connection.rtt ? String(connection.rtt) : 'unknown',
    ce: connection && connection.saveData ? connection.saveData : false,
    cf: gdpr ? md5(ips.join(',')).toString() : '',
  }
}
